/**
 * cart-tier.js
 *
 * Auto-applies quantity-tier discount codes (LIQUIDATION2..LIQUIDATION8)
 * based on cart item count.
 *
 * NERA25 note: the neighbour discount is now a SEGMENT-GATED AUTOMATIC
 * discount (ORDER class, productDiscounts: true). It stacks on top of the
 * tier code at checkout natively for logged-in customers tagged 'nera'.
 * This script must NOT touch NERA codes - it only manages tier codes.
 *
 * Mechanics (verified live):
 * - POST /cart/update.js { discount: "CODE" } sets cart codes.
 * - Dawn re-render: publish('cart-update', {source}) via assets/pubsub.js.
 *   PUB_SUB_EVENTS is a top-level const (NOT on window) - use the literal.
 * - All passes run through a serialized promise queue; concurrent passes
 *   with stale snapshots clobber each other (verified in simulation).
 */
(function () {
  'use strict';

  // The shop money_format hides cents; override for JS-rendered prices.
  if (window.Shopify) {
    window.Shopify.money_format = '€{{amount}}';
  }

  var TIERS = [
    { qty: 8, code: 'LIQUIDATION8' },
    { qty: 7, code: 'LIQUIDATION7' },
    { qty: 6, code: 'LIQUIDATION6' },
    { qty: 5, code: 'LIQUIDATION5' },
    { qty: 4, code: 'LIQUIDATION4' },
    { qty: 3, code: 'LIQUIDATION3' },
    { qty: 2, code: 'LIQUIDATION2' }
  ];

  function tierCodeForQuantity(qty) {
    for (var i = 0; i < TIERS.length; i++) {
      if (qty >= TIERS[i].qty) return TIERS[i].code;
    }
    return null; // qty < 2 -> no tier
  }

  // Codes this script manages. Anything else on the cart (customer-entered,
  // app-applied) is preserved untouched.
  var TIER_PREFIX = 'LIQUIDATION';

  function tierCodeIn(cart) {
    if (!cart.discount_codes || !Array.isArray(cart.discount_codes)) return null;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var c = (cart.discount_codes[i].code || '').toUpperCase();
      if (c.indexOf(TIER_PREFIX) === 0) return c;
    }
    return null;
  }

  function preservedCodes(cart) {
    var kept = [];
    if (cart.discount_codes && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (dc) {
        var c = (dc.code || '').toUpperCase();
        if (c.indexOf(TIER_PREFIX) !== 0) kept.push(c);
      });
    }
    return kept;
  }

  function root() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function applyDiscountCodes(codes) {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.filter(Boolean).join(',') })
    }).then(function (r) { return r.json(); });
  }

  function notifyDawn() {
    try {
      if (typeof window.publish === 'function') {
        window.publish('cart-update', { source: 'cart-tier' });
      }
    } catch (e) { /* non-fatal */ }
  }

  var queue = Promise.resolve();

  function enqueue(fn) {
    var run = queue.then(fn);
    queue = run.catch(function () { /* keep the queue alive on errors */ });
    return run;
  }

  function ensureTier(cart) {
    var desired = tierCodeForQuantity(cart.item_count || 0);
    var current = tierCodeIn(cart);
    if (desired === current) return Promise.resolve();
    var codes = [];
    if (desired) codes.push(desired);
    preservedCodes(cart).forEach(function (c) { codes.push(c); });
    if (codes.length === 0 && current === null) return Promise.resolve();
    return applyDiscountCodes(codes).then(function () {
      notifyDawn();
    });
  }

  function syncTier() {
    return enqueue(function () {
      return getCart().then(ensureTier);
    }).catch(function (err) {
      if (window.console) console.warn('cart-tier: sync failed', err);
    });
  }

  function boot() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncTier);
    } else {
      syncTier();
    }
    try {
      if (typeof window.subscribe === 'function') {
        window.subscribe('cart-update', function () {
          setTimeout(syncTier, 60);
        });
      }
    } catch (e) { /* non-fatal */ }
  }

  boot();
})();